import * as React from "react";
import { useLanguage } from "./Language";
import Paper from "@mui/material/Paper";
import InputBase from "@mui/material/InputBase";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import ClearIcon from "@mui/icons-material/Clear";
import TuneIcon from "@mui/icons-material/Tune";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import { Box, Stack, SxProps, useMediaQuery, useTheme } from "@mui/material";
import axios, { CancelTokenSource } from "axios";
import MicOffIcon from "@mui/icons-material/MicOff";
import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import {
  AuthorSuggestion,
  NewsFeedResource,
  PerformedSearchSuggestion,
  SuggestionParameters,
  SuggestionResult,
  TitleSuggestion,
} from "../dto/SuggestionResult";
import { SearchSuggestions } from "./SearchSuggestions";
import { TLCEventManager } from "../events/EventManager";
import { LS2PAC_CONSTANTS } from "../../main/constants";
import { LS2KIDS_CONSTANTS } from "../../../javascript/tlc/ls2kids/constants";
import { Speech } from "src/main/resources/javascript/tlc/speech/Speech";
import { SpeechTranscriptions } from "@tlc/ls2pac/app/SpeechTranslations";
import { Link } from "react-router-dom";
import { Branch } from "./LocationConfiguration";

export interface SearchEvent {
  searchTerm: string;
  searchType?: "author";
}

interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

const SEARCH_SUGGESTION_TIMEOUT = 250;

interface SearchBarProps {
  defaultSearchTerm?: string;
  voiceSearchEnabled: boolean;
  onClickSearch: (event: SearchEvent) => void;
  onClickTitle: (resource: TitleSuggestion | NewsFeedResource) => void;
  maxAuthorSuggestions?: number;
  maxTitleSuggestions?: number;
  maxPerformedSearchSuggestions?: number;
  branchFilters?: Branch[];
  syndeticsClientCode?: string;
  limitToKids: boolean;
  sx?: SxProps;
  autoFocus?: boolean;
}

// TODO, suggestions shouldn't render for all DBs.
// LS2PacSearchDatabaseManager.getCurrentDatabaseConfig().isDefault;
// This manager and dependencies rely on jQuery
// either include jQuery in Storybook, or setup event/observable for current DB config

// Future Refinements
// TODO More responsive — full screen on mobile, popover on deskop with dynamic max-height
// TODO close suggestions by hitting escape, or by clicking outside...
// TODO spelling suggestion service if there are zero results
// TODO flat when unfocused or not open
// TODO smoother transitions from loading to loaded
// TODO sort AuthorSuggestion by resourceCount?

export interface Suggestion {
  type: "author" | "title" | "newsFeed" | "performedSearch";
  value:
    | AuthorSuggestion
    | TitleSuggestion
    | NewsFeedResource
    | PerformedSearchSuggestion;
  key: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  onClickSearch,
  onClickTitle,
  syndeticsClientCode,
  branchFilters = [],
  defaultSearchTerm = "",
  voiceSearchEnabled = false,
  maxAuthorSuggestions = 5,
  maxTitleSuggestions = 3,
  maxPerformedSearchSuggestions = 4,
  limitToKids = false,
  autoFocus = false,
  sx,
}) => {
  const theme = useTheme();
  const extraSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const { translate } = useLanguage();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = React.useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(0);
  const [searchTerm, setSearchTerm] = React.useState<string>(
    defaultSearchTerm || ""
  );
  const [loading, setLoading] = React.useState<boolean>(true);
  const [cancelToken, setCancelToken] =
    React.useState<CancelTokenSource | null>(null);
  const [searchSuggestionTimeoutId, setSearchSuggestionTimeoutId] =
    React.useState<number | null>(null);
  const [suggestions, setSuggestions] = React.useState<Suggestion[]>([]);

  const showVoiceSearch =
    !limitToKids && voiceSearchEnabled && "webkitSpeechRecognition" in window;
  const [speech, setSpeech] = React.useState<Speech>();

  React.useEffect(() => {
    if (showVoiceSearch) {
      const speechInstance = new Speech(SpeechTranscriptions, {
        onStart: (event: Event) => {
          setListening(true);
          console.log("Speech callback, onStart", event);
        },
        onStop: () => {
          setListening(false);
          console.log("Speech callback, onStop");
        },
        onEnd: (event: Event) => {
          setListening(false);
          console.log("Speech callback, onEnd", event);
        },
        onFullResult: (transcript: string, event: SpeechRecognitionEvent) => {
          setSearchTerm(transcript);
          console.log("Speech callback, onFullResult", event, transcript);
        },
        onInterimResult: (
          transcript: string,
          event: SpeechRecognitionEvent
        ) => {
          setSearchTerm(transcript);
          console.log("Speech callback, onInterimResult", event, transcript);
        },
      });
      setSpeech(speechInstance);
    }
  }, [showVoiceSearch]);

  React.useEffect(() => {
    if (autoFocus === true && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  React.useEffect(() => {
    const clearSearchTerm = () => {
      setSearchTerm("");
      setOpen(false);
    };

    const events = [
      LS2PAC_CONSTANTS.Events.onLogoutResponse,
      LS2PAC_CONSTANTS.Events.onHome,
      LS2PAC_CONSTANTS.Events.onMyAccount,
      LS2PAC_CONSTANTS.Events.onAdvancedSearch,
      LS2KIDS_CONSTANTS.Events.onStartCategoryView,
      LS2KIDS_CONSTANTS.Events.onStartSeriesView,
    ];
    const eventListenerMap = new Map<string, number>();
    events.forEach((event) => {
      eventListenerMap.set(
        event,
        TLCEventManager.addListener(event, clearSearchTerm)
      );
    });
    eventListenerMap.set(
      LS2PAC_CONSTANTS.Events.onSearch,
      TLCEventManager.addListener(
        LS2PAC_CONSTANTS.Events.onSearch,
        (event: { searchString: string }) => {
          setListening(false);
          if (!event.searchString) {
            console.warn("onSearch fired without searchString");
          }
          if(!event.searchString || typeof event.searchString === "object"){
            setSearchTerm("");
          }
          else {
            try {
              const searchObject = JSON.parse(event.searchString);
              // searchString is JSON
              setSearchTerm("");
            }
            catch(e){
              // searchString is a normal string
              setSearchTerm(event.searchString);
            }
            
          }
          setOpen(false);
        }
      )
    );

    return () => {
      eventListenerMap.forEach((value, key) => {
        TLCEventManager.removeListener(key, value);
      });

      if (searchSuggestionTimeoutId) {
        clearTimeout(searchSuggestionTimeoutId);
      }
      if (cancelToken) {
        cancelToken.cancel("Component unmounted");
      }
    };
  }, []);

  const clear = translate("clear");
  //   const search = translate("search");
  const searchBarPlaceholder = translate("searchBarPlaceholder");
  const moreSearchOptions = translate("showSearchOptionsLinkText");
  const searchByVoice = translate("searchByVoice");

  const [listening, setListening] = React.useState<boolean>(false);

  const handleSearchTermChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (searchSuggestionTimeoutId) {
      clearTimeout(searchSuggestionTimeoutId);
      setSearchSuggestionTimeoutId(null);
    }
    const search = event.target.value;
    setSearchTerm(search);

    if (search.trim().length === 0) {
      setOpen(false);
      return;
    }
    setOpen(true);
    setSelectedIndex(0);

    setSearchSuggestionTimeoutId(
      window.setTimeout(() => {
        handleSearchSuggestions(search);
      }, SEARCH_SUGGESTION_TIMEOUT)
    );
  };

  const mapAuthorSuggestion = (
    authorSuggestion: AuthorSuggestion
  ): Suggestion => {
    return {
      type: "author",
      value: authorSuggestion,
      key: `author_${authorSuggestion.author}`,
    };
  };

  const mapTitleSuggestion = (titleSuggestion: TitleSuggestion): Suggestion => {
    return {
      type: "title",
      value: titleSuggestion,
      key: `title_${titleSuggestion.id}`,
    };
  };

  const mapNewsFeedResource = (
    newsFeedResource: NewsFeedResource
  ): Suggestion => {
    return {
      type: "newsFeed",
      value: newsFeedResource,
      key: `newsFeed_${newsFeedResource.id}`,
    };
  };

  const mapPerformedSearchSuggestion = (
    performedSearchSuggestion: PerformedSearchSuggestion
  ): Suggestion => {
    return {
      type: "performedSearch",
      value: performedSearchSuggestion,
      key: `performedSearch_${performedSearchSuggestion.term}`,
    };
  };

  const handleSearchSuggestions = async (term: string) => {
    // Cancel the previous request if it exists
    if (cancelToken) {
      cancelToken.cancel("Operation canceled by the user.");
    }

    // Create a new cancel token
    const newCancelToken = axios.CancelToken.source();
    setCancelToken(newCancelToken);

    const suggestionParams: SuggestionParameters = {
      maxAuthorSuggestions,
      maxTitleSuggestions,
      maxPerformedSearchSuggestions,
      term,
      branchFilters: branchFilters.map((branch) => branch.id.toString()),
      limitToKids,
    };

    try {
      setLoading(true);
      const response = await axios.post<SuggestionResult>(
        "/search/searchSuggestions",
        suggestionParams,
        { cancelToken: newCancelToken.token }
      );

      const {
        authorSuggestions,
        titleSuggestions,
        newsFeedSuggestions,
        performedSearchSuggestions,
      } = response.data;

      const otherSearchSuggestions = performedSearchSuggestions
        .filter((suggestion) => suggestion.term.trim() !== term.trim())
        .sort((a, b) => (a.timesSearched > b.timesSearched ? -1 : 1));

      // trim to maximums. API sometimes responds with more
      // discuss with team
      setSuggestions([
        ...otherSearchSuggestions
          .slice(
            0,
            Math.min(
              otherSearchSuggestions.length,
              maxPerformedSearchSuggestions
            )
          )
          .map(mapPerformedSearchSuggestion),
        ...authorSuggestions
          .slice(0, Math.min(authorSuggestions.length, maxAuthorSuggestions))
          .map(mapAuthorSuggestion),
        ...titleSuggestions
          .slice(0, Math.min(titleSuggestions.length, maxTitleSuggestions))
          .map(mapTitleSuggestion),
        ...newsFeedSuggestions.resources
          .slice(
            0,
            Math.min(newsFeedSuggestions.resources.length, maxTitleSuggestions)
          )
          .map(mapNewsFeedResource),
      ]);
      setLoading(false);
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request canceled", error.message);
      } else {
        throw error;
      }
    }
  };

  const submitSearch = (index: number): void => {
    if (index === 0) {
      const trimmedSearchTerm = searchTerm.trim();
      if (trimmedSearchTerm !== "") {
        onClickSearch({
          searchTerm: trimmedSearchTerm,
        });
        setOpen(false);
        return;
      }

      console.warn("Search term empty, ignoring");
      return;
    }

    try {
      const { type, value } = suggestions[index - 1];
      if (type === "author") {
        const { displayAuthor } = value as AuthorSuggestion;
        onClickSearch({
          searchTerm: displayAuthor,
          searchType: "author",
        });
        setSearchTerm(`author:"${displayAuthor}"`);
        setOpen(false);
        return;
      }

      if (type === "performedSearch") {
        const { term } = value as PerformedSearchSuggestion;
        onClickSearch({
          searchTerm: term,
        });
        setSearchTerm(term);
        setOpen(false);
        return;
      }

      onClickTitle(value as NewsFeedResource | TitleSuggestion);
      setOpen(false);
    } catch (e) {
      console.error(e);
    }
  };
  const handleClick = (index: number) => {
    setSelectedIndex(index);
    submitSearch(index); // pass the index to avoid any race conditions
  };

  // We use the ClickAwayListener and focus events of other buttons to handle blur of the search input
  // instead of add onBlur to the search input, so that users can click suggestions
  const onBlurSearchInput = (event: React.FocusEvent) => {
    setOpen(false);
  };

  const handleClickAway = () => {
    console.log("handleClickAway of SearchBar");
    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitSearch(selectedIndex);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (loading) {
        return;
      }
      setSelectedIndex((prevIndex) =>
        prevIndex === null ? 0 : Math.max(prevIndex - 1, 0)
      );
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      if (loading) {
        return;
      }
      setSelectedIndex((prevIndex) =>
        prevIndex === null ? 0 : Math.min(prevIndex + 1, suggestions.length)
      );
    }
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSearchTerm("");
    setSelectedIndex(0);
    setOpen(false);
    // reset focus on InputBase
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        sx={{
          ...sx,
          width: "100%",
          minWidth: "280px",
          position: "relative",
          minHeight: "48px",

          zIndex: 1000,
          // position: "absolute",

          maxWidth: "720px",
        }}
      >
        {/* <Box sx={{ position: "relative", minHeight: "48px", zIndex: 1000 }}> */}
        <Paper
          component="form"
          sx={{
            position: "absolute",
            p: "2px 4px",
            width: "100%",
          }}
        >
          <Stack direction="row" alignItems="center" sx={{ minHeight: "44px" }}>
            <InputBase
              startAdornment={
                !extraSmall && (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }
              sx={{
                ml: 1,
                flex: 1,
                "& .MuiInputBase-input::placeholder": {
                  opacity: 0.62, // 4.5:1 contrast
                },
              }}
              id="search-bar-input"
              name="search"
              value={searchTerm}
              onKeyDown={handleKeyDown}
              onChange={handleSearchTermChange}
              placeholder={listening ? "Listening..." : searchBarPlaceholder}
              inputProps={{ "aria-label": searchBarPlaceholder, ref: inputRef }}
            />
            {searchTerm.length > 0 && (
              <Tooltip title={clear} placement="bottom">
                <IconButton
                  onFocus={onBlurSearchInput}
                  onClick={handleClear}
                  type="button"
                  sx={{ p: "10px" }}
                  aria-label={clear}
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            )}
            {!limitToKids && (
              <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
            )}
            {/* TODO toggle aria-label and tooltip to something like to "Stop searching by voice" or "Stop Listening" */}
            {showVoiceSearch && (
              <Tooltip title={searchByVoice} placement="bottom">
                <IconButton
                  onFocus={onBlurSearchInput}
                  onClick={() => {
                    console.log(
                      "onClick Search by Voice, was listening: ",
                      listening
                    );
                    if (!listening) {
                      console.log("Start listening...");
                      setSearchTerm("");
                      speech.start();
                    } else {
                      console.log("Stop listening...");
                      // clear search term?
                      speech.stop();
                    }
                  }}
                  color="primary"
                  sx={{ p: "10px" }}
                  aria-label={searchByVoice}
                >
                  {listening ? <MicOffIcon /> : <KeyboardVoiceIcon />}
                </IconButton>
              </Tooltip>
            )}
            {!limitToKids && (
              <Tooltip title={moreSearchOptions} placement="bottom">
                <IconButton
                  onFocus={onBlurSearchInput}
                  component={Link}
                  to="/advanced-search/boolean"
                  color="primary"
                  sx={{ p: "10px" }}
                  aria-label={moreSearchOptions}
                >
                  <TuneIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          {open && (
            <React.Fragment>
              <Divider sx={{ height: 1, m: 0.5 }} orientation="horizontal" />

              <SearchSuggestions
                syndeticsClientCode={syndeticsClientCode}
                searchTerm={searchTerm}
                loading={loading}
                suggestions={suggestions}
                selectedIndex={selectedIndex}
                onClick={handleClick}
                maxAuthorSuggestions={maxAuthorSuggestions}
                maxPerformedSearchSuggestions={maxPerformedSearchSuggestions}
                maxTitleSuggestions={maxTitleSuggestions}
              />
            </React.Fragment>
          )}
        </Paper>
      </Box>
    </ClickAwayListener>
  );
};
